/**
 * AlertsTab Component
 *
 * Tab content for managing alert rules in user settings.
 * Allows creating, editing, deleting, and toggling alert rules.
 *
 * @module components/settings/AlertsTab
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlertStore } from '../../store/alertStore';
import { useAccountStore } from '../../store/accountStore';
import { useAuthStore } from '../../store/authStore';
import { Card, Button } from '../ui';
import { cn } from '../../utils/cn';
import type { AlertRule, AlertType, AlertOperator, AlertDirection, CreateAlertRuleRequest, UpdateAlertRuleRequest } from '@budgira/shared';
import { ALERT_TYPE_LABELS, ALERT_OPERATOR_LABELS, ALERT_DIRECTION_LABELS } from '@budgira/shared';

/**
 * AlertsTab component
 * 
 * Displays and manages alert rules with ownership-based permissions:
 * - Users can always create their own alerts
 * - Users can always modify/delete their own alerts (isOwner: true)
 * - Users can view/modify owner's alerts (isOwner: false) based on shared access permission
 */
export function AlertsTab() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'fr' ? 'fr' : 'en';

  const {
    rules,
    rulesLoading,
    rulesError,
    fetchRules,
    createRule,
    updateRule,
    deleteRule,
    toggleRuleActive,
    clearErrors,
  } = useAlertStore();

  const { accounts, fetchAccounts } = useAccountStore();
  const { canManageAlerts } = useAuthStore();
  
  // Check if user has write permission for owner's alerts (not their own)
  const canEditOwnerAlerts = canManageAlerts();
  
  // Helper to check if user can edit a specific rule
  const canEditRule = (rule: AlertRule) => {
    // User can always edit their own alerts
    if (rule.isOwner === true || rule.isOwner === undefined) return true;
    // For owner's alerts, check permission
    return canEditOwnerAlerts;
  };

  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateAlertRuleRequest>({
    name: '',
    type: 'balance',
    operator: 'lt',
    threshold: 0,
    accountId: null,
    direction: 'debit',
    notifyInApp: true,
    notifyEmail: false,
  });
  const [formError, setFormError] = useState<string | null>(null);

  // Load rules and accounts on mount
  useEffect(() => {
    fetchRules();
    fetchAccounts();
  }, [fetchRules, fetchAccounts]);

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      type: 'balance',
      operator: 'lt',
      threshold: 0,
      accountId: null,
      direction: 'debit',
      notifyInApp: true,
      notifyEmail: false,
    });
    setFormError(null);
    setEditingRule(null);
    setShowForm(false);
    clearErrors();
  };

  // Open form for new rule
  const handleNewRule = () => {
    resetForm();
    setShowForm(true);
  };

  // Open form for editing
  const handleEditRule = (rule: AlertRule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      type: rule.type,
      operator: rule.operator,
      threshold: rule.threshold,
      accountId: rule.accountId,
      direction: rule.direction || 'debit',
      notifyInApp: rule.notifyInApp,
      notifyEmail: rule.notifyEmail,
    });
    setFormError(null);
    setShowForm(true);
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Validation
    if (!formData.name.trim()) {
      setFormError(t('alerts.form.errors.nameRequired', 'Le nom est requis'));
      return;
    }

    if (formData.type === 'operation_amount' && formData.threshold < 0) {
      setFormError(t('alerts.form.errors.thresholdPositive', 'Le seuil doit être positif'));
      return;
    }

    if (editingRule) {
      // Update existing rule
      const updateData: UpdateAlertRuleRequest = {
        name: formData.name,
        type: formData.type,
        operator: formData.operator,
        threshold: formData.threshold,
        accountId: formData.accountId,
        direction: formData.type === 'operation_amount' ? formData.direction : null,
        notifyInApp: formData.notifyInApp,
        notifyEmail: formData.notifyEmail,
      };
      const result = await updateRule(editingRule.id, updateData);
      if (result) {
        resetForm();
      } else {
        setFormError(rulesError || t('alerts.form.errors.updateFailed', 'Erreur lors de la mise à jour'));
      }
    } else {
      // Create new rule
      const createData: CreateAlertRuleRequest = {
        ...formData,
        direction: formData.type === 'operation_amount' ? formData.direction : null,
      };
      const result = await createRule(createData);
      if (result) {
        resetForm();
      } else {
        setFormError(rulesError || t('alerts.form.errors.createFailed', 'Erreur lors de la création'));
      }
    }
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    const success = await deleteRule(id);
    if (success) {
      setDeleteConfirm(null);
    }
  };

  // Handle toggle active
  const handleToggleActive = async (id: string, isActive: boolean) => {
    await toggleRuleActive(id, isActive);
  };

  // Format threshold for display
  const formatThreshold = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  };

  // Get account name by ID
  const getAccountName = (accountId: string | null) => {
    if (!accountId) return t('alerts.allAccounts', 'Tous les comptes');
    const account = accounts.find((a) => a.id === accountId);
    if (account?.name) return account.name;
    // If account not found in list, show a shortened version of ID or generic text
    // This can happen when user has read-only access and limited account visibility
    return t('alerts.specificAccount', 'Compte spécifique');
  };

  // Get operator symbol
  const getOperatorSymbol = (operator: AlertOperator) => {
    switch (operator) {
      case 'lt': return '<';
      case 'gt': return '>';
      case 'lte': return '≤';
      case 'gte': return '≥';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {t('alerts.settings.title', 'Règles d\'alerte')}
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('alerts.settings.description', 'Configurez des alertes personnalisées basées sur vos soldes et opérations.')}
          </p>
        </div>
        {!showForm && (
          <Button onClick={handleNewRule}>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t('alerts.settings.addRule', 'Nouvelle alerte')}
          </Button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <Card>
          <form onSubmit={handleSubmit} className="space-y-4">
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-4">
              {editingRule
                ? t('alerts.form.titleEdit', 'Modifier l\'alerte')
                : t('alerts.form.titleNew', 'Nouvelle alerte')
              }
            </h3>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('alerts.form.name', 'Nom de l\'alerte')} *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('alerts.form.namePlaceholder', 'Ex: Alerte solde bas')}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
            </div>

            {/* Account */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('alerts.form.account', 'Compte')}
              </label>
              <select
                value={formData.accountId || ''}
                onChange={(e) => setFormData({ ...formData, accountId: e.target.value || null })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              >
                <option value="">{t('alerts.form.allAccounts', 'Tous les comptes (global)')}</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('alerts.form.type', 'Type de surveillance')} *
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as AlertType })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              >
                <option value="balance">{ALERT_TYPE_LABELS.balance[lang]}</option>
                <option value="forecast_balance">{ALERT_TYPE_LABELS.forecast_balance[lang]}</option>
                <option value="operation_amount">{ALERT_TYPE_LABELS.operation_amount[lang]}</option>
              </select>
            </div>

            {/* Direction (only for operation_amount) */}
            {formData.type === 'operation_amount' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('alerts.form.direction', 'Type d\'opération')} *
                </label>
                <select
                  value={formData.direction || 'debit'}
                  onChange={(e) => setFormData({ ...formData, direction: e.target.value as AlertDirection })}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                >
                  <option value="debit">{ALERT_DIRECTION_LABELS.debit[lang]}</option>
                  <option value="credit">{ALERT_DIRECTION_LABELS.credit[lang]}</option>
                  <option value="both">{ALERT_DIRECTION_LABELS.both[lang]}</option>
                </select>
              </div>
            )}

            {/* Operator and Threshold */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('alerts.form.operator', 'Condition')} *
                </label>
                <select
                  value={formData.operator}
                  onChange={(e) => setFormData({ ...formData, operator: e.target.value as AlertOperator })}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                >
                  <option value="lt">{ALERT_OPERATOR_LABELS.lt[lang]}</option>
                  <option value="lte">{ALERT_OPERATOR_LABELS.lte[lang]}</option>
                  <option value="gt">{ALERT_OPERATOR_LABELS.gt[lang]}</option>
                  <option value="gte">{ALERT_OPERATOR_LABELS.gte[lang]}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('alerts.form.threshold', 'Seuil (€)')} *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min={formData.type === 'operation_amount' ? 0.01 : undefined}
                  value={formData.threshold}
                  onChange={(e) => setFormData({ ...formData, threshold: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Notifications */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t('alerts.form.notifications', 'Notifications')}
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.notifyInApp}
                    onChange={(e) => setFormData({ ...formData, notifyInApp: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    {t('alerts.form.notifyInApp', 'Notification dans l\'application (cloche)')}
                  </span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.notifyEmail}
                    onChange={(e) => setFormData({ ...formData, notifyEmail: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    {t('alerts.form.notifyEmail', 'Notification par email')}
                  </span>
                </label>
              </div>
            </div>

            {/* Error */}
            {formError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                {formError}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={resetForm}>
                {t('common.cancel', 'Annuler')}
              </Button>
              <Button type="submit" disabled={rulesLoading}>
                {rulesLoading ? t('common.saving', 'Enregistrement...') : t('common.save', 'Enregistrer')}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Rules list */}
      {rulesLoading && !showForm ? (
        <Card>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" />
          </div>
        </Card>
      ) : rules.length === 0 && !showForm ? (
        <Card>
          <div className="text-center py-8">
            <BellIcon className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
            <p className="text-slate-500 dark:text-slate-400">
              {t('alerts.settings.noRules', 'Aucune règle d\'alerte configurée')}
            </p>
            <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
              {t('alerts.settings.noRulesHint', 'Créez votre première alerte pour être notifié automatiquement.')}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {rules.map((rule) => (
            <Card key={rule.id} className={cn(!rule.isActive && 'opacity-60')}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                {/* Rule info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-slate-900 dark:text-slate-100 truncate">
                      {rule.name}
                    </h4>
                    {!rule.isActive && (
                      <span className="px-2 py-0.5 text-xs bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300 rounded">
                        {t('alerts.inactive', 'Inactive')}
                      </span>
                    )}
                    {rule.isOwner === false && (
                      <span className="px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded">
                        {t('alerts.ownerAlert', 'Alerte propriétaire')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    {ALERT_TYPE_LABELS[rule.type][lang]} {getOperatorSymbol(rule.operator)} {formatThreshold(rule.threshold)}
                    {rule.type === 'operation_amount' && rule.direction && (
                      <span className="ml-1">
                        ({ALERT_DIRECTION_LABELS[rule.direction][lang]})
                      </span>
                    )}
                  </p>
                  <div className="text-sm text-slate-400 dark:text-slate-500 mt-0.5">
                    {getAccountName(rule.accountId)}
                    {(rule.notifyInApp || rule.notifyEmail) && ' • '}
                    {rule.notifyInApp && 'Application'}
                    {rule.notifyInApp && rule.notifyEmail && ' • '}
                    {rule.notifyEmail && 'Email'}
                  </div>
                </div>

                {/* Actions - only show if user can edit this rule */}
                {canEditRule(rule) && (
                  <div className="flex items-center gap-2">
                    {/* Toggle active */}
                    <button
                      onClick={() => handleToggleActive(rule.id, !rule.isActive)}
                      className={cn(
                        'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2',
                        rule.isActive ? 'bg-violet-600' : 'bg-slate-200 dark:bg-slate-600'
                      )}
                      title={rule.isActive ? t('alerts.deactivate', 'Désactiver') : t('alerts.activate', 'Activer')}
                    >
                      <span
                        className={cn(
                          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                          rule.isActive ? 'translate-x-5' : 'translate-x-0'
                        )}
                      />
                    </button>

                    {/* Edit */}
                    <button
                      onClick={() => handleEditRule(rule)}
                      className="p-2 text-slate-500 hover:text-violet-600 dark:text-slate-400 dark:hover:text-violet-400 transition-colors"
                      title={t('common.edit', 'Modifier')}
                    >
                      <EditIcon className="w-4 h-4" />
                    </button>

                    {/* Delete */}
                    {deleteConfirm === rule.id ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleDelete(rule.id)}
                          className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          title={t('common.confirmDelete', 'Confirmer')}
                        >
                          <CheckIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
                          title={t('common.cancel', 'Annuler')}
                        >
                          <XIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(rule.id)}
                        className="p-2 text-slate-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400 transition-colors"
                        title={t('common.delete', 'Supprimer')}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Error message */}
      {rulesError && !formError && (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          {rulesError}
        </div>
      )}
    </div>
  );
}

// Icons
const PlusIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const BellIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);

const EditIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const TrashIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const XIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export default AlertsTab;
